import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Auth } from 'aws-amplify';

// Define types
interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: false,
  error: null,
};

// Mock users for local development
const mockUsers = [
  {
    username: 'admin',
    password: 'password',
    attributes: {
      given_name: 'Admin',
      family_name: 'User',
      email: 'admin@example.com',
      'custom:role': 'admin',
    },
  },
  {
    username: 'presenter',
    password: 'password',
    attributes: {
      given_name: 'Presenter',
      family_name: 'User',
      email: 'presenter@example.com',
      'custom:role': 'presenter',
    },
  },
  {
    username: 'voter',
    password: 'password',
    attributes: {
      given_name: 'Voter',
      family_name: 'User',
      email: 'voter@example.com',
      'custom:role': 'voter',
    },
  },
];

// Mock authentication functions
const mockAuth = {
  signIn: async (username: string, password: string) => {
    const user = mockUsers.find(
      (u) => u.username === username && u.password === password
    );
    
    if (!user) {
      throw new Error('Invalid username or password');
    }
    
    localStorage.setItem('currentUser', JSON.stringify(user));
    return user;
  },
  signOut: async () => {
    localStorage.removeItem('currentUser');
    return null;
  },
  currentAuthenticatedUser: async () => {
    const user = localStorage.getItem('currentUser');
    if (!user) {
      throw new Error('No authenticated user');
    }
    return JSON.parse(user);
  },
};

// Async thunks
export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ username, password }: { username: string; password: string }, { rejectWithValue }) => {
    try {
      // Use mock authentication for local development
      // In production, this would use Auth.signIn from AWS Amplify
      const user = await mockAuth.signIn(username, password);
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sign in');
    }
  }
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      // Use mock authentication for local development
      // In production, this would use Auth.signOut from AWS Amplify
      await mockAuth.signOut();
      return null;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sign out');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      // Use mock authentication for local development
      // In production, this would use Auth.currentAuthenticatedUser from AWS Amplify
      const user = await mockAuth.currentAuthenticatedUser();
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'No authenticated user');
    }
  }
);

// Create slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Sign In
    builder.addCase(signIn.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(signIn.fulfilled, (state, action: PayloadAction<any>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(signIn.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Sign Out
    builder.addCase(signOut.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(signOut.fulfilled, (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(signOut.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Get Current User
    builder.addCase(getCurrentUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCurrentUser.fulfilled, (state, action: PayloadAction<any>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getCurrentUser.rejected, (state, action) => {
      state.isAuthenticated = false;
      state.user = null;
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
